import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    PaginationState,
    Row,
    RowSelectionState,
    SortingState,
    Table,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';
import {useVirtualizer} from '@tanstack/react-virtual';
import {FiFileText} from "react-icons/fi";

// App types (strict)
import {DocumentReadWReferences, DocumentSearch, SortCriteria, TagType, isTranscriptExchangeTag} from '../../schemas';
import {DocumentConverter, TableItemForGridFormat} from './data';
import {useEnhancedDocumentsContext} from '../../contexts/doc_contexts';
import {GridView, useNavigationContext} from '../../contexts/NavContext/nav-context';
import {useGroupContext} from '../../contexts/group_context';
import {useFolderContext} from '../../contexts/folder_context';
import {TagFilter} from "../../schemas/boolean_filtering";

import {FilterableHeader, TagColumnHeader} from './headers';
import {TagOptionWithMeta} from './tag-filter-dropdown';
import {useTagContext} from "../../contexts/tag_context";
import {useAIStateContext} from "../../contexts/ai_chat_state_context";
import {DisplaySettingsDropdown} from "./display-settings-dropdown";
import {
    GridColumnDef,
    getDocumentColumns, computeInitialVisibility, getExplanationColumns
} from "./column-definitions";

// Multi-filter system
import {useMultiFilters} from "./use-multi-filters";
import {FilterPanel, FilterPanelHandle, FilterSectionType} from "./filter-panel";
import {ColumnFilter} from "./filter-types";

// Events for auto-refresh
import {TAG_UPDATE_COMPLETED, DOCUMENT_UPLOAD_COMPLETED} from "../../events";

// ---------- Utils ----------
const clsx = (...parts: Array<string | false | null | undefined>): string => parts.filter(Boolean).join(' ');

export const TanStackDocGrid: React.FC<{ gridView?: GridView }> = ({gridView}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const clickTimerRef = useRef<number | undefined>(undefined);
    const lastSelectedIndexRef = useRef<number | null>(null);

    const PAGE_SIZE = 50;

    // Contexts
    const {
        document: activeDocument,
        lastVisitedDocument,
        getDocument,
        setDocument,
        documentSearch,
        pullDocuments,
        updateDocumentSearch,
        setSelectedDocIds,
        docTotalCount,
    } = useEnhancedDocumentsContext()!;

    const {currentFolder} = useFolderContext()!;
    const {currentGroup} = useGroupContext()!;
    const {docSelectedListenersActive, setIsDocExpanded, changeDocView} = useNavigationContext()!;
    const {tagMap, tagFilters, setTagFilters} = useTagContext()!;
    const {currentChat} = useAIStateContext();
    // Stabilize: only re-derive when the chat ID actually changes (not on object ref changes from replaceChat)
    const currentChatId = currentChat?.id ?? null;

    // Local state
    const [data, setData] = useState<TableItemForGridFormat[]>([]);
    const [_rowCount, setRowCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [_processedSearch, setProcessedSearch] = useState<DocumentSearch>({});

    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    // Multi-filter state (advanced filtering with search types)
    const multiFilterState = useMultiFilters();

    const colVisKey = `colVis:${currentGroup?.id ?? 'default'}`;
    const colVisKeyRef = useRef(colVisKey);
    const [columnVisibility, setColumnVisibilityRaw] = useState<VisibilityState>(() => {
        try {
            const saved = localStorage.getItem(colVisKey);
            return saved ? JSON.parse(saved) : {};
        } catch { return {}; }
    });
    // Reload from localStorage when group changes
    useEffect(() => {
        if (colVisKeyRef.current !== colVisKey) {
            colVisKeyRef.current = colVisKey;
            try {
                const saved = localStorage.getItem(colVisKey);
                if (saved) setColumnVisibilityRaw(JSON.parse(saved));
            } catch { /* ignore */ }
        }
    }, [colVisKey]);
    const setColumnVisibility = useCallback((updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
        setColumnVisibilityRaw(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            try { localStorage.setItem(colVisKeyRef.current, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    }, []);
    const [pagination, setPagination] = useState<PaginationState>({pageIndex: 0, pageSize: PAGE_SIZE});
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});


    // Column menu state
    const [colMenuOpen, setColMenuOpen] = useState<boolean>(false);
    const colMenuRef = useRef<HTMLDivElement | null>(null);

    // Chat filter toggle for Document Requests
    const [filterByCurrentChat, setFilterByCurrentChat] = useState<boolean>(false);

    // Set of tag IDs that have explanation columns shown
    const [explanationColumnIds, setExplanationColumnIds] = useState<Set<string>>(new Set());

    // Toggle explanation column for a tag
    const toggleExplanationColumn = useCallback((tagId: string) => {
        setExplanationColumnIds((prev) => {
            const next = new Set(prev);
            if (next.has(tagId)) {
                next.delete(tagId);
            } else {
                next.add(tagId);
            }
            return next;
        });
    }, []);

    // Count document requests for current chat (used to auto-enable filter)
    const currentChatRequestCount = useMemo(() => {
        if (!currentChatId) return 0;
        return Array.from(tagMap.values()).filter(
            (tag) => tag.type === TagType.REQUEST_SET && tag.chat_id === currentChatId
        ).length;
    }, [tagMap, currentChatId]);

    // Update filter state when currentChat changes
    // - Enable filter when chat opens AND has requests
    // - Disable filter when no chat is selected
    useEffect(() => {
        if (currentChatId && currentChatRequestCount > 0) {
            setFilterByCurrentChat(true);
        } else {
            setFilterByCurrentChat(false);
        }
    }, [currentChatId, currentChatRequestCount]);

    // Clear local data when group OR folder changes to prevent showing stale
    // tag filters from a previous folder reducing the next folder's results.
    // Also patch stateRef so the fetchPage effect (which fires in the same
    // commit on folder/group change) reads cleared filters instead of the
    // stale value still sitting in tag_context until the next render.
    useEffect(() => {
        setData([]);
        setRowCount(0);
        setPagination((p) => ({...p, pageIndex: 0}));
        setRowSelection({});
        if (setTagFilters) setTagFilters(null);
        stateRef.current = { ...stateRef.current, tagFilters: null };
    }, [currentGroup?.id, currentFolder?.id, setTagFilters]);

    // Keep context search in sync but avoid loops
    useEffect(() => {
        const shouldFetch = documentSearch?.search_origin !== 'grid';
        if (!shouldFetch || !documentSearch) return;
        setProcessedSearch(documentSearch);
        // Derive the correct page from the search offset instead of always resetting to 0
        const offset = documentSearch.offset ?? 0;
        const pageSize = pagination.pageSize;
        const pageIndex = pageSize > 0 ? Math.floor(offset / pageSize) : 0;
        setPagination((p) => ({...p, pageIndex}));
    }, [documentSearch]);

    // Use refs for values that change but shouldn't trigger re-fetch
    const contextRef = useRef({ updateDocumentSearch, pullDocuments });
    contextRef.current = { updateDocumentSearch, pullDocuments };

    const stateRef = useRef({ pagination, currentFolder, currentGroup, tagFilters, multiFilterState, sorting });
    stateRef.current = { pagination, currentFolder, currentGroup, tagFilters, multiFilterState, sorting };

    // Server-side fetch - completely stable function using refs
    const fetchPage = useCallback(async () => {
        const { pagination, currentFolder, currentGroup, tagFilters, multiFilterState, sorting } = stateRef.current;
        const { updateDocumentSearch, pullDocuments } = contextRef.current;

        setIsLoading(true);
        try {
            const offset = pagination.pageIndex * pagination.pageSize;
            const limit = pagination.pageSize;

            // Use the multi-filter system for document_filters
            const documentFilters = multiFilterState.toDocumentFilters();

            // Convert TanStack sorting to backend sort_criteria
            const sortCriteria: SortCriteria[] = sorting.map((s) => ({
                field: s.id,
                direction: s.desc ? "desc" : "asc",
            }));

            const payload = {
                offset,
                limit,
                group_id: currentGroup?.id,
                folder_tag_id: currentFolder?.id,
                search_origin: 'grid',
                document_filters: documentFilters,
                tag_filters: Array.from(tagFilters?.values() || []).flat(),
                sort_criteria: sortCriteria.length > 0 ? sortCriteria : undefined,
            } as DocumentSearch;
            const nextSearch = await updateDocumentSearch(payload);
            console.log('Grid fetch with payload', nextSearch);
            const docMap = await pullDocuments(nextSearch);
            const rows = Array.from(docMap.values()).map((doc) => DocumentConverter.toTableItem(doc));
            setData(rows);
            setRowCount(docMap.size);
        } finally {
            setIsLoading(false);
        }
    }, []); // Empty deps - uses refs for everything

    // Track previous values to detect actual changes
    const prevValuesRef = useRef({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        groupId: currentGroup?.id,
        folderId: currentFolder?.id,
        sorting: sorting,
    });
    const initialFetchDone = useRef(false);

    // Fetch on pagination, group, folder, or sorting changes (not on filter text changes - only Apply button)
    useEffect(() => {
        const prev = prevValuesRef.current;
        const sortingChanged = JSON.stringify(prev.sorting) !== JSON.stringify(sorting);
        const changed =
            prev.pageIndex !== pagination.pageIndex ||
            prev.pageSize !== pagination.pageSize ||
            prev.groupId !== currentGroup?.id ||
            prev.folderId !== currentFolder?.id ||
            sortingChanged;

        // Update refs
        prevValuesRef.current = {
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            groupId: currentGroup?.id,
            folderId: currentFolder?.id,
            sorting: sorting,
        };

        // Only fetch if something actually changed, or it's the initial fetch
        if (!initialFetchDone.current || changed) {
            initialFetchDone.current = true;
            void fetchPage();
        }
    }, [pagination.pageIndex, pagination.pageSize, currentGroup?.id, currentFolder?.id, sorting, fetchPage]);


    //     // Dedicated effect for tag filter changes — reset to page 1 and re-fetch
    // // This handles both filter panel Apply and MetadataPalette entity clicks
    // const prevTagFiltersRef = useRef(tagFilters);
    // useEffect(() => {
    //     if (prevTagFiltersRef.current === tagFilters) return;
    //     prevTagFiltersRef.current = tagFilters;
    //     if (!initialFetchDone.current) return;
    //     setPagination((p) => ({...p, pageIndex: 0}));
    //     void fetchPage();
    // }, [tagFilters, fetchPage]);

    // Tag filter changes are applied when the user clicks "Apply Filters" in the
    // filter panel, NOT automatically. This keeps entity-tag clicks from the
    // MetadataPalette from triggering immediate searches — the user can toggle
    // multiple entity filters and then apply them in one batch.

    // Listen for tag/document updates and auto-refresh the grid
    useEffect(() => {
        const handleRefresh = () => {
            console.log('Grid: Auto-refresh triggered by event');
            void fetchPage();
        };
        window.addEventListener(TAG_UPDATE_COMPLETED, handleRefresh);
        window.addEventListener(DOCUMENT_UPLOAD_COMPLETED, handleRefresh);
        return () => {
            window.removeEventListener(TAG_UPDATE_COMPLETED, handleRefresh);
            window.removeEventListener(DOCUMENT_UPLOAD_COMPLETED, handleRefresh);
        };
    }, [fetchPage]);

    // Define columns (strict)
    // Base columns + dynamic explanation columns
    const columns = useMemo<GridColumnDef[]>(() => {
        const baseColumns = getDocumentColumns();
        const explainCols = getExplanationColumns(explanationColumnIds, tagMap);
        return [...baseColumns, ...explainCols];
    }, [gridView, explanationColumnIds, tagMap]);

    const initialVisibility = useMemo(
        () => computeInitialVisibility(columns, gridView),
        [columns, gridView]
    );

    // Apply initial visibility only when no saved state exists.
    // Saved column preferences from localStorage take priority.
    const initialVisibilityApplied = useRef(false);
    useEffect(() => {
        const hasSaved = (() => {
            try { return !!localStorage.getItem(colVisKey); } catch { return false; }
        })();
        if (!initialVisibilityApplied.current) {
            if (!hasSaved) {
                setColumnVisibility(initialVisibility);
            }
            initialVisibilityApplied.current = true;
        } else if (!hasSaved) {
            // Subsequent changes (e.g. explanation column toggled): merge
            setColumnVisibility(prev => ({...prev, ...initialVisibility}));
        }
    }, [initialVisibility]);

    // Auto-show Testimony column when the current data has at least one transcript
    useEffect(() => {
        const hasTranscript = data.some(row => row.type === 'TRANSCRIPT');
        setColumnVisibility(prev => ({...prev, transcript_codes: hasTranscript}));
    }, [data]);

    const table = useReactTable<TableItemForGridFormat>({
        data,
        columns,
        state: {sorting, columnFilters, columnVisibility, pagination, rowSelection},
        onSortingChange: (updater) => {
            const next = typeof updater === 'function' ? updater(sorting) : updater;
            setSorting(next);
            // Reset to first page when sorting changes
            setPagination((p) => ({...p, pageIndex: 0}));
        },
        onColumnFiltersChange: (updater) => {
            const next = typeof updater === 'function' ? updater(columnFilters) : updater;
            setColumnFilters(next);
            setPagination((p) => ({...p, pageIndex: 0}));
        },
        onColumnVisibilityChange: setColumnVisibility,
        onPaginationChange: setPagination,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        manualFiltering: true,
        manualSorting: true, // Server-side sorting
        pageCount: Math.ceil(Math.max(docTotalCount, 1) / pagination.pageSize),
        enableRowSelection: true,
        enableMultiSort: true, // Allow sorting by multiple columns (shift+click)
        columnResizeMode: 'onChange', // immediate resizing (or 'onEnd' for smoother)
    });

    // Virtualizer (row ~100px)
    const rowVirtualizer = useVirtualizer({
        count: table.getRowModel().rows.length,
        getScrollElement: () => containerRef.current,
        estimateSize: () => 50,
        measureElement: el => el.getBoundingClientRect().height,
    });


    // Single vs double click
    const handleRowSingleClick = useCallback(async (row: Row<TableItemForGridFormat>) => {
        const id = row.original.id as string;
        const doc: DocumentReadWReferences | undefined = await getDocument(id);
        if (doc) {
            setDocument(doc);
            changeDocView(doc);  // Update URL to include docId
        }
    }, [getDocument, setDocument, changeDocView]);

    const handleRowDoubleClick = useCallback(async (row: Row<TableItemForGridFormat>) => {
        window.clearTimeout(clickTimerRef.current);
        const id = row.original.id as string;
        const doc = await getDocument(id);
        if (!doc) return;
        setDocument(doc);
        setIsDocExpanded(true);
    }, [getDocument, setDocument, setIsDocExpanded]);

    const selectRange = useCallback((start: number, end: number, additive: boolean) => {
        const rows = table.getRowModel().rows;
        const next: RowSelectionState = additive ? {...table.getState().rowSelection} : {};
        const s = Math.min(start, end);
        const e = Math.max(start, end);
        for (let i = s; i <= e; i++) next[rows[i].id] = true;
        table.setRowSelection(next);
    }, [table]);

    const onRowClick = useCallback(
        (e: React.MouseEvent, row: Row<TableItemForGridFormat>, rowIndex: number) => {
            window.clearTimeout(clickTimerRef.current);
            const isRange = e.shiftKey;
            const isAdditive = (e as React.MouseEvent).ctrlKey || (e as React.MouseEvent).metaKey;

            if (isRange && lastSelectedIndexRef.current !== null) {
                selectRange(lastSelectedIndexRef.current, rowIndex, isAdditive);
            } else if (isAdditive) {
                const cur = {...table.getState().rowSelection};
                const id = row.id;
                if (row.getIsSelected()) delete cur[id];
                else cur[id] = true;
                table.setRowSelection(cur);
                lastSelectedIndexRef.current = rowIndex;
            } else {
                table.setRowSelection({[row.id]: true});
                lastSelectedIndexRef.current = rowIndex;
                clickTimerRef.current = window.setTimeout(() => {
                    void handleRowSingleClick(row);
                }, 200);
            }
        },
        [handleRowSingleClick, selectRange, table]
    );

    useEffect(() => {
        const docSelect = activeDocument || lastVisitedDocument;
        if (!docSelect) return;

        const idx = table.getRowModel().rows.findIndex(
            (r) => r.original.id === docSelect.id
        );
        if (idx >= 0) {
            const row = table.getRowModel().rows[idx];

            // ✅ overwrite selection state, don’t accumulate
            table.setRowSelection({[row.id]: true});

            rowVirtualizer.scrollToIndex(idx, {align: "center"});
            lastSelectedIndexRef.current = idx; // keep anchor aligned
        }
    }, [activeDocument, lastVisitedDocument, table, rowVirtualizer]);


    // Available codes for filtering (all TAG type, with transcript tags marked)
    const codesOptions: TagOptionWithMeta[] = useMemo(
        () =>
            Array.from(tagMap.values())
                .filter((tag) => tag.type === TagType.TAG)
                .map((tag) => ({
                    id: tag.id,
                    name: tag.tag_name,
                    isCurrentChat: false,
                    isTranscript: isTranscriptExchangeTag(tag),
                    docCount: tag.document_count,
                }))
                .sort((a, b) => {
                    if (a.isTranscript && !b.isTranscript) return 1;
                    if (!a.isTranscript && b.isTranscript) return -1;
                    return a.name.localeCompare(b.name);
                }),
        [tagMap]
    );

    // All request options with current chat marked (sorted: current chat first, then newest first)
    const requestOptions: TagOptionWithMeta[] = useMemo(
        () =>
            Array.from(tagMap.values())
                .filter((tag) => tag.type === TagType.REQUEST_SET)
                .map((tag) => ({
                    id: tag.id,
                    name: tag.tag_name,
                    isCurrentChat: currentChatId ? tag.chat_id === currentChatId : false,
                    docCount: tag.document_count,
                }))
                .sort((a, b) => {
                    if (a.isCurrentChat && !b.isCurrentChat) return -1;
                    if (!a.isCurrentChat && b.isCurrentChat) return 1;
                    const tagA = tagMap.get(a.id);
                    const tagB = tagMap.get(b.id);
                    if (tagA && tagB) {
                        return new Date(tagB.created_at).getTime() - new Date(tagA.created_at).getTime();
                    }
                    return 0;
                }),
        [tagMap, currentChatId]
    );

    // All entity options (grouped by entity type, then alphabetical)
    const entityOptions: TagOptionWithMeta[] = useMemo(
        () =>
            Array.from(tagMap.values())
                .filter((tag) => tag.type === TagType.ENTITY)
                .map((tag) => ({
                    id: tag.id,
                    name: tag.tag_name,
                    isCurrentChat: false,
                    entityType: tag.entity_type,
                    docCount: tag.document_count,
                }))
                .sort((a, b) => {
                    const typeCompare = (a.entityType ?? '').localeCompare(b.entityType ?? '');
                    if (typeCompare !== 0) return typeCompare;
                    return a.name.localeCompare(b.name);
                }),
        [tagMap]
    );

    // Auto-set explanation column to the newest request for the current chat.
    // Runs on chat change AND when request options update (new doc request created).
    const currentChatRequestIds = useMemo(
        () => requestOptions.filter(o => o.isCurrentChat).map(o => o.id).join(','),
        [requestOptions],
    );

    useEffect(() => {
        if (currentChatId && requestOptions.length > 0) {
            // requestOptions is sorted: current chat first, newest first
            const latestCurrentChatRequest = requestOptions.find((opt) => opt.isCurrentChat);
            if (latestCurrentChatRequest) {
                setExplanationColumnIds(new Set([latestCurrentChatRequest.id]));
            } else {
                setExplanationColumnIds(new Set());
            }
        } else if (!currentChatId) {
            setExplanationColumnIds(new Set());
        }
    }, [currentChatId, currentChatRequestIds]);

    // Handler for adding filter from column header
    const handleAddFilterFromHeader = useCallback((columnId: string) => {
        multiFilterState.addFilter(columnId);
    }, [multiFilterState]);

    // Handler for removing filter from column header
    const handleRemoveFilterFromHeader = useCallback((filterId: string) => {
        multiFilterState.removeFilter(filterId);
    }, [multiFilterState]);

    // ---------- Header renderer ----------
    const renderHeader = (tbl: Table<TableItemForGridFormat>, colId: string, headerLabel: string) => {
        const column = tbl.getColumn(colId);

        // Use TagColumnHeader for tag-based columns
        if (colId === 'document_requests') {
            return (
                <TagColumnHeader
                    label={headerLabel}
                    filterType="requests"
                    onFilterClick={handleQuickFilterClick}
                    activeFilterCount={requestsFilterCount}
                    column={column}
                    activeFilters={activeRequestFilters}
                    onRemoveFilter={handleRemoveTagFilter}
                />
            );
        }
        if (colId === 'codes') {
            return (
                <TagColumnHeader
                    label={headerLabel}
                    filterType="codes"
                    onFilterClick={handleQuickFilterClick}
                    activeFilterCount={codesFilterCount}
                    column={column}
                    activeFilters={activeCodeFilters}
                    onRemoveFilter={handleRemoveTagFilter}
                />
            );
        }
        if (colId === 'entities') {
            return (
                <TagColumnHeader
                    label={headerLabel}
                    filterType="entities"
                    onFilterClick={handleQuickFilterClick}
                    activeFilterCount={entitiesFilterCount}
                    column={column}
                    activeFilters={activeEntityFilters}
                    onRemoveFilter={handleRemoveTagFilter}
                />
            );
        }

        // Default: use FilterableHeader for other columns
        return (
            <FilterableHeader
                columnId={colId}
                label={headerLabel}
                filters={multiFilterState.filters}
                onAddFilter={handleAddFilterFromHeader}
                onRemoveFilter={handleRemoveFilterFromHeader}
                column={column}
            />
        );
    };

    // Close column menu on outside click / Esc
    useEffect(() => {
        const onDoc = (e: MouseEvent) => {
            if (!colMenuRef.current) return;
            if (e.target instanceof Node && !colMenuRef.current.contains(e.target)) {
                setColMenuOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setColMenuOpen(false);
        };
        window.addEventListener('mousedown', onDoc);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('mousedown', onDoc);
            window.removeEventListener('keydown', onKey);
        };
    }, []);


    const addSelectedIdsToContext = () => {
        const selectedIds = table
            .getSelectedRowModel()
            .rows.map((r) => r.original.id as string);
        setSelectedDocIds(selectedIds);
    };
    const selectedCount = table.getSelectedRowModel().rows.length;

    // State to force filter panel to expand when a saved search is applied
    const [filterPanelForceExpand, setFilterPanelForceExpand] = useState(false);

    // Ref for the FilterPanel to call imperative methods
    const filterPanelRef = useRef<FilterPanelHandle>(null);

    // Calculate active filters per type for quick-filter buttons and inline display
    const activeRequestFilters = useMemo(() => {
        if (!tagFilters) return [];
        return requestOptions.filter(opt => tagFilters.has(opt.id));
    }, [tagFilters, requestOptions]);

    const activeCodeFilters = useMemo(() => {
        if (!tagFilters) return [];
        return codesOptions.filter(opt => tagFilters.has(opt.id));
    }, [tagFilters, codesOptions]);

    const activeEntityFilters = useMemo(() => {
        if (!tagFilters) return [];
        return entityOptions.filter(opt => tagFilters.has(opt.id));
    }, [tagFilters, entityOptions]);

    const requestsFilterCount = activeRequestFilters.length;
    const codesFilterCount = activeCodeFilters.length;
    const entitiesFilterCount = activeEntityFilters.length;

    // Handler for quick-filter button clicks in column headers
    const handleQuickFilterClick = useCallback((filterType: FilterSectionType) => {
        filterPanelRef.current?.expandAndScrollTo(filterType);
    }, []);

    // Handler for removing a tag filter from column header
    const handleRemoveTagFilter = useCallback((tagId: string) => {
        setTagFilters((prev) => {
            if (!prev) return null;
            const next = new Map(prev);
            next.delete(tagId);
            return next.size > 0 ? next : null;
        });
    }, []);

    // Handler for applying a saved search
    const handleApplySavedSearch = useCallback((restoredFilters: ColumnFilter[], tagFiltersArray: TagFilter[]) => {
        // Set the multi-filter state with the restored filters
        multiFilterState.setFiltersDirectly(restoredFilters);
        // Convert tag filters array to Map format expected by the grid
        const tagFilterMap = new Map<string, TagFilter[]>();
        for (const tf of tagFiltersArray) {
            const existing = tagFilterMap.get(tf.tag_id) || []
            tagFilterMap.set(tf.tag_id, [...existing, tf]);
        }
        setTagFilters(tagFilterMap.size > 0 ? tagFilterMap : null);
        setPagination((p) => ({...p, pageIndex: 0}));
        // Force filter panel to expand so user can see the restored filters
        setFilterPanelForceExpand(true);
        // Reset the flag after a short delay so it can be triggered again
        setTimeout(() => setFilterPanelForceExpand(false), 100);
    }, [multiFilterState]);

    // Dynamic column template: first column fixed 120px (id), others auto
    const headerTemplate = table
        .getVisibleLeafColumns()
        .map((col) => `${col.getSize()}px`)
        .join(" ");

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-2 border-b border-[#a1a1b6] p-2">
                <button
                    className="rounded-xl border border-[#a1a1b6] px-3 py-1 text-sm text-[#1c1c24] hover:bg-[#ececf3] transition-colors"
                    onClick={() => {
                        void fetchPage();
                    }}
                title="Refresh"
                >
                    Refresh
                </button>

                {docSelectedListenersActive && <button
                    className="flex items-center gap-2 rounded-xl border border-[#a1a1b6] px-3 py-1 text-sm text-[#1c1c24] hover:bg-[#ececf3] transition-colors disabled:opacity-50"
                    disabled={selectedCount === 0}
                    onClick={() => {
                        if (selectedCount > 0) {
                            addSelectedIdsToContext();
                        }
                    }}
                >
                    <FiFileText className="text-[#6d6d8a]"/>
                    {selectedCount > 0
                        ? `Click to add ${selectedCount} document${selectedCount > 1 ? "s" : ""} to chat context`
                        : "Select to Add to context"}
                </button>}
                {selectedCount > 0 &&
                    <button
                        className="ml-2 rounded-xl border border-[#a1a1b6] px-3 py-1 text-sm text-[#1c1c24] hover:bg-[#ececf3] transition-colors"
                        onClick={() => table.resetRowSelection()}
                    >
                        Clear selection
                    </button>
                }

                <div className="ml-auto relative flex items-center gap-2">
                    {/* Display Settings */}
                    <DisplaySettingsDropdown
                        filterByCurrentChat={filterByCurrentChat}
                        setFilterByCurrentChat={setFilterByCurrentChat}
                        explanationColumnIds={explanationColumnIds}
                        toggleExplanationColumn={toggleExplanationColumn}
                    />

                    {/* Columns menu */}
                    <div className="relative">
                        <button
                            className="rounded-xl border border-[#a1a1b6] px-3 py-1 text-sm text-[#1c1c24] hover:bg-[#ececf3] transition-colors"
                            onClick={() => setColMenuOpen(o => !o)}
                        >
                            Columns ▾
                        </button>
                        {colMenuOpen && (
                            <div ref={colMenuRef}
                                 className="absolute right-0 top-full z-20 mt-2 w-64 rounded-xl border border-[#a1a1b6] bg-white shadow-lg">
                                <div className="max-h-72 overflow-y-auto p-2">
                                    {table.getAllLeafColumns().filter((c) => c.id !== 'id').map((c) => (
                                        <label key={c.id} className="flex items-center gap-2 px-2 py-1 hover:bg-[#ececf3] rounded cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={c.getIsVisible()}
                                                onChange={(e) => c.toggleVisibility(e.target.checked)}
                                                className="accent-[#8c8ca3]"
                                            />
                                            <span
                                                className="text-sm text-[#1c1c24] truncate">{String(c.columnDef.header ?? c.id)}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="flex items-center justify-between gap-2 border-t border-[#a1a1b6] p-2">
                                    <button className="rounded border border-[#a1a1b6] px-2 py-1 text-xs text-[#1c1c24] hover:bg-[#ececf3] transition-colors"
                                            onClick={() => table.getAllLeafColumns().forEach((c) => c.toggleVisibility(true))}>
                                        Show all
                                    </button>
                                    <button className="rounded border border-[#a1a1b6] px-2 py-1 text-xs text-[#1c1c24] hover:bg-[#ececf3] transition-colors"
                                            onClick={() => table.getAllLeafColumns().forEach((c) => c.id === 'id' ? c.toggleVisibility(true) : c.toggleVisibility(false))}>
                                        Hide all
                                    </button>
                                    <button className="rounded border border-[#a1a1b6] px-2 py-1 text-xs text-[#1c1c24] hover:bg-[#ececf3] transition-colors"
                                            onClick={() => {
                                                try { localStorage.removeItem(colVisKey); } catch { /* ignore */ }
                                                setColumnVisibility(computeInitialVisibility(columns, gridView));
                                            }}>
                                        Reset defaults
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Advanced Filter Panel */}
            <FilterPanel
                ref={filterPanelRef}
                filterState={multiFilterState}
                onSearch={() => {
                    setPagination((p) => ({...p, pageIndex: 0}));
                    void fetchPage();
                }}
                forceExpand={filterPanelForceExpand}
                tagFilters={tagFilters}
                setTagFilters={setTagFilters}
                codesOptions={codesOptions}
                requestOptions={requestOptions}
                entityOptions={entityOptions}
                onApplySavedSearch={handleApplySavedSearch}
            />

            {/* Table */}
            <div className="flex-1 min-h-0 flex flex-col relative">
                {/* Subtle loading indicator */}
                {isLoading && (
                    <div className="absolute top-0 left-0 right-0 z-20">
                        <div className="h-0.5 w-full bg-slate-100 overflow-hidden">
                            <div className="h-full w-1/3 bg-blue-400 animate-shimmer" />
                        </div>
                    </div>
                )}
                {/* Scrollable table area (horizontal + vertical) */}
                <div ref={containerRef} className="flex-1 min-h-0 overflow-auto">
                    {/* Inner container with min-width to enable horizontal scroll */}
                    <div style={{ minWidth: 'max-content' }}>
                        {/* Header */}
                        <div className="sticky top-0 z-10 border-b bg-slate-50/80 backdrop-blur-sm p-1"
                             style={{display: 'grid', gridTemplateColumns: headerTemplate, gap: '0.3rem'}}>
                            {table.getFlatHeaders().map((header, headerIndex, headersArray) => (
                                <div
                                    key={header.id}
                                    className={clsx(
                                        "relative flex flex-col flex-grow",
                                        header.column.getIsVisible() ? "" : "hidden",
                                        headerIndex < headersArray.length - 1 ? "border-r border-gray-200" : ""
                                    )}
                                    style={{
                                        width: header.column.getSize(),
                                        minWidth: header.column.columnDef.minSize ?? 60,
                                        maxWidth: header.column.columnDef.maxSize ?? 1000,
                                    }}
                                >
                                    {header.isPlaceholder ? null : renderHeader(table, header.column.id, String(flexRender(header.column.columnDef.header, header.getContext())))}

                                    {/* Resizer handle */}
                                    {header.column.getCanResize() && (
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={clsx(
                                                "absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none transition-colors",
                                                header.column.getIsResizing() ? "bg-blue-400" : "hover:bg-gray-300"
                                            )}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Body */}
                        <div style={{height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative'}}>
                            {rowVirtualizer.getVirtualItems().map((vItem) => {
                                const row = table.getRowModel().rows[vItem.index];
                                const visibleCells = row.getVisibleCells();
                                const cellCount = visibleCells.length;
                                return (
                                    <div
                                        key={row.id}
                                        ref={rowVirtualizer.measureElement}
                                        data-index={vItem.index}
                                        onClick={(e) => onRowClick(e, row, vItem.index)}
                                        onDoubleClick={() => void handleRowDoubleClick(row)}
                                        className={clsx(
                                            'absolute left-0 border-y border-gray-100 px-1 py-2 items-center transition-colors duration-150 cursor-pointer',
                                            row.getIsSelected()
                                                ? 'bg-blue-50 hover:bg-blue-100/70'
                                                : 'bg-white hover:bg-slate-50'
                                        )}
                                        style={{
                                            transform: `translateY(${vItem.start}px)`,
                                            display: 'grid',
                                            gridTemplateColumns: headerTemplate,
                                            gap: '0.3rem',
                                            alignItems: 'start',
                                            minWidth: 'max-content',
                                        }}
                                    >
                                        {visibleCells.map((cell, cellIndex) => (
                                            <div
                                                key={cell.id}
                                                className={clsx(
                                                    "min-w-0 self-start px-1",
                                                    cellIndex < cellCount - 1 ? 'border-r border-slate-100' : ''
                                                )}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Footer / Pagination - outside scrollable area */}
                <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/60 p-2 text-sm flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <button className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-100 disabled:opacity-40 transition-colors"
                                onClick={() => table.setPageIndex(0)}
                                disabled={!table.getCanPreviousPage()}>{'⏮'}</button>
                        <button className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-100 disabled:opacity-40 transition-colors"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}>{'◀'}</button>
                        <button className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-100 disabled:opacity-40 transition-colors"
                                onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>{'▶'}</button>
                        <button className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-100 disabled:opacity-40 transition-colors"
                                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                disabled={!table.getCanNextPage()}>{'⏭'}</button>
                        <span
                            className="ml-2">Page <strong>{pagination.pageIndex + 1}</strong> of <strong>{table.getPageCount()}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            className="rounded border px-2 py-1"
                            value={pagination.pageSize}
                            onChange={(e) => setPagination((p) => ({
                                ...p,
                                pageSize: Number(e.target.value),
                                pageIndex: 0
                            }))}
                        >
                            {[25, 50, 100, 200].map((ps) => (
                                <option key={ps} value={ps}>Show {ps}</option>
                            ))}
                        </select>
                        <span>{isLoading ? 'Loading…' : `${docTotalCount} total row(s)`}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TanStackDocGrid;
